import React, { useState, useEffect } from 'react';
import { Plus, BookOpen, Clock, Eye, Edit2, Trash2, X, Brain, AlertTriangle } from 'lucide-react';
import { Link } from "react-router-dom";
import axios from 'axios';
import { useAuth } from '../../context/studentAuthContext';
import Navigation from "../../../src/components/Navbar"
import Footer from '../../components/Footer';

const BASE_URL = process.env.REACT_APP_BASE_URL;

const LearningJournal = () => {
  const { student } = useAuth();
  const [entries, setEntries] = useState([]);
  const [loading, setLoading] = useState(true);
  const [lastUpdated, setLastUpdated] = useState('');

  const [selectedEntry, setSelectedEntry] = useState(null);
  const [isViewModalOpen, setIsViewModalOpen] = useState(false);
  const [isEditModalOpen, setIsEditModalOpen] = useState(false);
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
  const [entryToDelete, setEntryToDelete] = useState(null);
  const [deleteLoading, setDeleteLoading] = useState(false);

  useEffect(() => {
    const fetchJournals = async () => {
      if (!student?.id) return;

      try {
        const res = await axios.get(
          `${BASE_URL}/api/students/journals/${student.id}`
        );

        setEntries(res.data.data || []);
        
        // Calculate last updated
        if (res.data.data && res.data.data.length > 0) {
          const latest = new Date(Math.max(...res.data.data.map(e => new Date(e.updated_at || e.created_at))));
          const daysAgo = Math.floor((new Date() - latest) / (1000 * 60 * 60 * 24));
          setLastUpdated(daysAgo === 0 ? 'Today' : daysAgo === 1 ? 'Yesterday' : `${daysAgo} days ago`);
        } else {
          setLastUpdated('No entries yet');
        }
      } catch (err) {
        console.error("Failed to fetch journals:", err);
      } finally {
        setLoading(false);
      }
    };

    fetchJournals();
  }, [student]);

  // Format date function
  const formatDate = (dateString) => {
    if (!dateString) return 'Unknown date';
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', { 
      year: 'numeric', 
      month: 'short', 
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  // Action handlers
  const handleView = (entry) => {
    setSelectedEntry(entry);
    setIsViewModalOpen(true);
  };

  const handleEdit = (entry) => {
    setSelectedEntry(entry);
    setIsEditModalOpen(true);
  };

  const handleDeleteClick = (entry) => {
    setEntryToDelete(entry);
    setIsDeleteModalOpen(true);
  };

  const handleDeleteConfirm = async () => {
    if (!entryToDelete) return;
    
    setDeleteLoading(true);
    try {
      await axios.delete(
        `${BASE_URL}/api/students/delete-journal/${entryToDelete.id}`
      );

      setEntries(prev => prev.filter(entry => entry.id !== entryToDelete.id));
      setIsDeleteModalOpen(false);
      setEntryToDelete(null);
    } catch (err) {
      console.error("Delete failed:", err);
      alert("Failed to delete entry");
    } finally {
      setDeleteLoading(false);
    }
  };

  const handleDeleteCancel = () => {
    setIsDeleteModalOpen(false);
    setEntryToDelete(null);
  };

  const handleSaveEdit = async () => {
    try {
      await axios.put(
        `${BASE_URL}/api/students/update-journal/${selectedEntry.id}`,
        {
          title: selectedEntry.title,
          instructor: selectedEntry.instructor,
          content: selectedEntry.entry,
        }
      );

      setEntries(prev =>
        prev.map(entry =>
          entry.id === selectedEntry.id ? selectedEntry : entry
        )
      );

      setIsEditModalOpen(false);
    } catch (err) {
      console.error("Update failed:", err);
      alert("Failed to update entry");
    }
  };

  return (
    <>
    <Navigation/>
   
    <div className="min-h-screen mt-20 bg-gray-50 py-10 px-4 sm:px-6 lg:px-8 font-sans">
       
      <div className="max-w-3xl mx-auto">

        {/* Header */}
        <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center mb-10 gap-4">
          <h1 className="text-3xl font-bold text-gray-900 tracking-tight">Learning Journal</h1>
          <Link to="/new-journal">
            <button className="flex items-center gap-2 bg-[#1976D2] hover:bg-blue-700 text-white px-5 py-2.5 rounded-lg font-medium transition-all shadow-sm active:scale-95">
              <Plus size={18} />
              New Journal Entry
            </button>
          </Link>
        </div>

        {/* Stats */}
        <section className="mb-10">
          <h2 className="text-sm font-semibold text-gray-500 uppercase tracking-wider mb-3">Your Journal At A Glance</h2>
          <div className="bg-white border border-gray-200 rounded-xl p-6 flex flex-col sm:flex-row gap-6 sm:gap-12 shadow-sm">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-blue-50 rounded-lg text-blue-600">
                <BookOpen size={20} />
              </div>
              <div>
                <p className="text-xs text-gray-500 font-medium">Entries Written</p>
                <p className="text-xl font-bold text-gray-900">{entries.length}</p>
              </div>
            </div>
            <div className="flex items-center gap-3">
              <div className="p-2 bg-orange-50 rounded-lg text-orange-600">
                <Clock size={20} />
              </div>
              <div>
                <p className="text-xs text-gray-500 font-medium">Last Updated</p>
                <p className="text-xl font-bold text-gray-900">{lastUpdated}</p>
              </div>
            </div>
          </div>
        </section>

        {/* Reflection Prompts */}
        <section className="mb-10 bg-white border border-gray-200 rounded-xl p-6 shadow-sm">
          <h2 className="text-sm font-semibold text-gray-500 uppercase tracking-wider mb-4">Reflection Prompts</h2>
          <ul className="space-y-3">
            {["What new concept did you learn today?", "What was challenging?", "What tasks have you been assigned?", "What's your goal for the next session?"].map((prompt, idx) => (
              <li key={idx} className="flex items-start gap-3 text-gray-700">
                <span className="mt-1.5 h-1.5 w-1.5 rounded-full bg-blue-500 shrink-0" />
                <span className="text-[15px]">{prompt}</span>
              </li>
            ))}
          </ul>
        </section>

        {/* Entries */}
        <section className="space-y-6">
          <h2 className="text-sm font-semibold text-gray-500 uppercase tracking-wider mb-4">Your Entries</h2>
          
          {loading ? (
            <div className="text-center py-10 text-gray-400 font-medium">Loading your reflections...</div>
          ) : entries.length === 0 ? (
            <div className="bg-white border-2 border-dashed border-gray-200 rounded-xl p-10 text-center">
              <p className="text-gray-400 font-medium">No entries yet. Start your learning journey today!</p>
            </div>
          ) : (
            entries.map((entry) => (
              <div key={entry.id} className="group bg-white border border-gray-200 rounded-xl p-6 shadow-sm hover:border-blue-300 transition-colors">
                <div className="flex justify-between items-start mb-4">
                  <div>
                    <h3 className="text-lg font-bold text-gray-900 mb-1">{entry.title}</h3>
                    <p className="text-xs text-gray-400 font-medium italic">
                      Instructor: {entry.instructor || 'Self-Study'} • {formatDate(entry.created_at)}
                    </p>
                  </div>
                  {entry.sentiment && (
                    <span className="px-3 py-1 bg-blue-50 text-blue-600 text-[10px] font-black uppercase tracking-wider rounded-full border border-blue-100">
                      Mood: {entry.sentiment}
                    </span>
                  )}
                </div>

                <hr className="border-gray-100 mb-4" />

                <p className="text-gray-600 leading-relaxed mb-6 line-clamp-3">
                  {entry.entry}
                </p>

                {entry.ai_feedback && (
                  <div className="bg-gradient-to-r from-blue-50 to-indigo-50 border-l-4 border-blue-500 p-4 rounded-r-lg mb-6">
                    <div className="flex items-center gap-2 mb-2">
                       <Brain size={16} className="text-blue-600" />
                       <span className="text-xs font-bold text-blue-900 uppercase">PeerMate AI Insight</span>
                    </div>
                    <p className="text-sm text-blue-800 leading-relaxed italic">
                      "{entry.ai_feedback}"
                    </p>
                  </div>
                )}

                <div className="flex flex-wrap gap-3">
                  <button 
                    onClick={() => handleView(entry)} 
                    className="flex items-center gap-1.5 px-4 py-2 text-sm font-medium text-gray-600 bg-gray-50 hover:bg-blue-50 hover:text-blue-600 rounded-md transition-colors border border-transparent hover:border-blue-100"
                  >
                    <Eye size={14} /> View
                  </button>
                  <button 
                    onClick={() => handleEdit(entry)} 
                    className="flex items-center gap-1.5 px-4 py-2 text-sm font-medium text-gray-600 bg-gray-50 hover:bg-blue-50 hover:text-blue-600 rounded-md transition-colors border border-transparent hover:border-blue-100"
                  >
                    <Edit2 size={14} /> Edit
                  </button>
                  <button 
                    onClick={() => handleDeleteClick(entry)} 
                    className="flex items-center gap-1.5 px-4 py-2 text-sm font-medium text-red-500 bg-gray-50 hover:bg-red-50 rounded-md transition-colors border border-transparent hover:border-red-100"
                  >
                    <Trash2 size={14} /> Delete
                  </button>
                </div>
              </div>
            ))
          )}
        </section>

        {/* Footer */}
        <footer className="mt-16 text-center border-t border-gray-200 pt-8">
          <p className="text-sm text-gray-400 italic">Your journal is private to you.</p>
        </footer>
      </div>

      {/* View Modal */}
      {isViewModalOpen && selectedEntry && (
        <div className="fixed inset-0 bg-black/40 backdrop-blur-sm z-50 flex items-center justify-center p-4">
          <div className="bg-white rounded-lg w-full max-w-2xl shadow-2xl overflow-y-auto max-h-[90vh] p-6 relative animate-in zoom-in duration-200">
            <button 
              onClick={() => setIsViewModalOpen(false)} 
              className="absolute top-4 right-4 text-gray-400 hover:text-gray-600 transition-colors"
            >
              <X size={20} />
            </button>
            
            <div className="mb-6">
              <h2 className="text-2xl font-bold text-gray-900 mb-2 pr-8">{selectedEntry.title}</h2>
              <div className="flex flex-wrap items-center gap-3 text-sm">
                <span className="text-gray-500">
                  Instructor: <span className="font-medium text-gray-700">{selectedEntry.instructor || 'Self-Study'}</span>
                </span>
                <span className="text-gray-300">•</span>
                <span className="text-gray-500">
                  {formatDate(selectedEntry.created_at)}
                </span>
              </div>
            </div>

            <div className="prose max-w-none">
              <p className="text-gray-700 leading-relaxed whitespace-pre-wrap">
                {selectedEntry.entry}
              </p>
            </div>

            {selectedEntry.ai_feedback && (
              <div className="mt-8 bg-gradient-to-r from-blue-50 to-indigo-50 border-l-4 border-blue-500 p-5 rounded-r-lg">
                <div className="flex items-center gap-2 mb-3">
                  <Brain size={18} className="text-blue-600" />
                  <span className="text-xs font-bold text-blue-900 uppercase tracking-wider">PeerMate AI Insight</span>
                </div>
                <p className="text-sm text-blue-800 leading-relaxed italic">
                  "{selectedEntry.ai_feedback}"
                </p>
              </div>
            )}

            <div className="mt-8 flex justify-end">
              <button
                onClick={() => setIsViewModalOpen(false)}
                className="px-6 py-2 bg-gray-100 hover:bg-gray-200 text-gray-700 rounded-lg font-medium transition-colors"
              >
                Close
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Edit Modal */}
      {isEditModalOpen && selectedEntry && (
        <div className="fixed inset-0 bg-black/40 backdrop-blur-sm z-50 flex items-center justify-center p-4">
          <div className="bg-white rounded-lg w-full max-w-2xl shadow-2xl overflow-y-auto max-h-[90vh] p-6 relative animate-in zoom-in duration-200">
            <button 
              onClick={() => setIsEditModalOpen(false)} 
              className="absolute top-4 right-4 text-gray-400 hover:text-gray-600 transition-colors"
            >
              <X size={20} />
            </button>

            <h2 className="text-2xl font-bold text-gray-900 mb-6">Edit Journal Entry</h2>

            <div className="space-y-5">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Title
                </label>
                <input
                  type="text"
                  value={selectedEntry.title}
                  onChange={(e) =>
                    setSelectedEntry({ ...selectedEntry, title: e.target.value })
                  }
                  className="w-full border border-gray-300 rounded-lg px-4 py-2.5 focus:ring-2 focus:ring-blue-500 focus:border-transparent outline-none text-sm"
                  placeholder="Entry title"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Instructor
                </label>
                <input
                  type="text"
                  value={selectedEntry.instructor || ''}
                  onChange={(e) =>
                    setSelectedEntry({ ...selectedEntry, instructor: e.target.value })
                  }
                  className="w-full border border-gray-300 rounded-lg px-4 py-2.5 focus:ring-2 focus:ring-blue-500 focus:border-transparent outline-none text-sm"
                  placeholder="Instructor name (optional)"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Journal Entry
                </label>
                <textarea
                  value={selectedEntry.entry}
                  onChange={(e) =>
                    setSelectedEntry({ ...selectedEntry, entry: e.target.value })
                  }
                  rows={10}
                  className="w-full border border-gray-300 rounded-lg p-4 focus:ring-2 focus:ring-blue-500 focus:border-transparent outline-none resize-none text-sm"
                  placeholder="Write your journal entry..."
                />
              </div>
            </div>

            <div className="flex gap-3 mt-8">
              <button
                onClick={handleSaveEdit}
                className="flex-1 bg-[#1976D2] hover:bg-blue-700 text-white py-3 rounded-lg font-medium transition-colors"
              >
                Save Changes
              </button>
              <button
                onClick={() => setIsEditModalOpen(false)}
                className="flex-1 bg-gray-100 hover:bg-gray-200 text-gray-700 py-3 rounded-lg font-medium transition-colors"
              >
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Delete Confirmation Modal */}
      {isDeleteModalOpen && entryToDelete && (
        <div className="fixed inset-0 bg-black/40 backdrop-blur-sm z-50 flex items-center justify-center p-4">
          <div className="bg-white rounded-lg w-full max-w-md shadow-2xl p-6 relative animate-in zoom-in duration-200">
            <div className="text-center mb-6">
              <div className="mx-auto w-12 h-12 bg-red-100 rounded-full flex items-center justify-center mb-4">
                <AlertTriangle size={24} className="text-red-600" />
              </div>
              <h3 className="text-xl font-bold text-gray-900 mb-2">Delete Journal Entry</h3>
              <p className="text-gray-500">
                Are you sure you want to delete "{entryToDelete.title}"? This action cannot be undone.
              </p>
            </div>

            <div className="flex gap-3">
              <button
                onClick={handleDeleteConfirm}
                disabled={deleteLoading}
                className={`flex-1 bg-red-600 hover:bg-red-700 text-white py-3 rounded-lg font-medium transition-colors ${
                  deleteLoading ? 'opacity-50 cursor-not-allowed' : ''
                }`}
              >
                {deleteLoading ? 'Deleting...' : 'Delete'}
              </button>
              <button
                onClick={handleDeleteCancel}
                disabled={deleteLoading}
                className="flex-1 bg-gray-100 hover:bg-gray-200 text-gray-700 py-3 rounded-lg font-medium transition-colors"
              >
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
    <Footer/>
    </>
  );
};

export default LearningJournal;